import React, { useContext, useState } from 'react';
import {
  Autocomplete, Box, Checkbox, Chip, IconButton, InputAdornment, styled,
  TextField, Tooltip, Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FormDataContext } from '../../contexts/FormDataContext';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
export const IntakeAutocomplete = (
  {
    label,
    fullLabel,
    options,
    optionIdPropertyName,
    selectedTaxonomyTermIds,
    setSelectedTaxonomyTermIds,
    ...additionalProps
  }
) => {
  const termIdPropertyName = optionIdPropertyName ?? "id";
  const [inputFocused, setInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [value, setValue] = useState(() => {
    if (selectedTaxonomyTermIds) {
      return options.filter(option => {
        return selectedTaxonomyTermIds.includes(option[termIdPropertyName]);
      })
    }
    return [];
  });
  const {
    setIsFormDataChanged,
  } = useContext(FormDataContext);
  const defaultGetOptionLabel = option => option.name;
  const getOptionLabel = additionalProps.getOptionLabel || defaultGetOptionLabel;
  const handleOnChange = (event, newValue) => {
    const hasTaxonomyStateVariables =
      selectedTaxonomyTermIds && setSelectedTaxonomyTermIds;
    if (hasTaxonomyStateVariables) {
      const termIdsToRemove = options.map(option => option[termIdPropertyName]);
      const filteredTaxonomyTermIds = selectedTaxonomyTermIds.filter(
        termId => !termIdsToRemove.includes(termId)
      );
      const newTermIds = newValue.map(term => term[termIdPropertyName]);
      const selectedTermIds = filteredTaxonomyTermIds.concat(newTermIds);
      setSelectedTaxonomyTermIds(selectedTermIds);
      setIsFormDataChanged(true);
      setValue(newValue);
    }
  };
  const handleOnInputChange = (event, newInputValue, reason) => {
    if (reason !== "reset") {
      setInputValue(newInputValue);
    }
  };
  const ClearAll = () => {
    const ClearButton = styled("div")({
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
    });
    const ClearText = styled("span")({
      fontSize: "small",
      marginLeft: "2px",
    });
    return (
      <ClearButton>
        <ClearIcon sx={{fontSize:"1rem"}}/>
        <ClearText>clear all</ClearText>
      </ClearButton>
    )
  };
  const ClearSearch = () => {
    return (
      <Tooltip title="Clear search term" arrow>
        <IconButton
          aria-label="Clear search term"
          onClick={() => setInputValue('')}
          edge="end"
          size="small"
          sx={{
            marginRight: "3px",
            padding: "6px",
            marginTop: "1px",
        }}
        >
          <ClearIcon/>
        </IconButton>
      </Tooltip>
    );
  };
  const PopupIcon = () => {
    return (
      <>
        {isPopupOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
      </>
    )
  };
  return (
    <Autocomplete
      {...additionalProps}
      sx={{
        "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
          paddingRight: "39px",
        }
      }}
      renderTags={(tagValue, getTagProps) =>
        <div className="intakeChips">
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      maxWidth: { md: 350, xs: 220 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getOptionLabel(option)}
                  </Box>
                }
                {...tagProps}
              />
            );
          })}
          </Box>
        </div>
      }
      onOpen={() => setIsPopupOpen(true)}
      onClose={() => setIsPopupOpen(false)}
      popupIcon={<PopupIcon/>}
      multiple
      disableCloseOnSelect
      value={value}
      onChange={handleOnChange}
      inputValue={inputValue}
      onInputChange={handleOnInputChange}
      options={options}
      disabled={additionalProps.disabled || false}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={props.id}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {getOptionLabel(option)}
          {option.definition && (
            <Tooltip title={<Typography dangerouslySetInnerHTML={{ __html: option.definition }}/>} enterTouchDelay={1}>
              <IconButton>
                <InfoOutlinedIcon sx={{fontSize: "1.25rem"}}/>
              </IconButton>
            </Tooltip>
          )}
        </li>
      )}
      style={{ width: "100%", marginTop: "15px" }}
      renderInput={(params) => {
        const inputIsEmpty = () => {
          return !inputFocused && !inputValue;
        };
        return (
          <TextField
            {...params}
            label={inputIsEmpty() ? (fullLabel ?? `Search ${label}…`) : ''}
            placeholder={fullLabel ?? `Search ${label}…`}
            InputLabelProps={{
              ...params.InputLabelProps,
              shrink: false
            }}
            InputProps={{
              ...params.InputProps,
              inputProps: {
                ...params.inputProps,
                "aria-label": (!inputIsEmpty() ? (fullLabel ?? `Search ${label}…`) : "")
              },
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                </>
              ),
              endAdornment: (
                <>
                  {inputValue &&
                    <InputAdornment position="end">
                      <ClearSearch/>
                    </InputAdornment>
                  }
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => {
              setInputFocused(false);
              setInputValue("");
            }}
            sx={{
              ".MuiInputBase-root": {
                display: "flex",
                flexWrap: "wrap"
              },
              ".intakeChips": {
                order: 3,
                paddingRight: "40px"
              },
              ".MuiInputAdornment-positionEnd": {
                order: 2
              },
              ".MuiInputBase-input.MuiOutlinedInput-input": {
                width: "calc(100% - 95px)",
                order: 1,
                // "&:not(:placeholder-shown)": {
                //   width: "calc(100% - 160px)"
                // },
              },
              ".MuiAutocomplete-endAdornment": {
                height: "calc(100% - 45px)",
                top: 45,
              },
              ".MuiAutocomplete-clearIndicator": {
                top: "10px",
                marginRight: "-23px",
                "&:hover": {
                  backgroundColor: "transparent"
                }
              },
              ".MuiAutocomplete-popupIndicator": {
                top: "-30px",
                right: "6px",
                transform: "scale(1.3)",
                // padding: "5px",
              },
              ".MuiInputLabel-root": {
                paddingLeft: "30px"
              }
            }}
          />
        );
      }}
      clearIcon={<ClearAll/>}
      clearText={`Clear all selected ${label ?? "options"}`}
    />
  );
};